import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { appointmentService } from '../services/api';
import { Appointment } from '../types';
import { 
  formatDate, 
  formatTime, 
  getStatusColor, 
  getStatusLabel, 
  getSimplifiedEmailStatus, 
  getSimplifiedSMSStatus
} from '../utils/statusUtils'; 
import AppointmentCard from '../components/AppointmentCard';
import socketService from '../services/socketService';
import './AppointmentList.css';

const AppointmentList: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<Appointment[]>([]); 
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all'); // 'all' par défaut pour charger les non-terminés
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [communicationFilter, setCommunicationFilter] = useState<string>('all');

  // --- Fonction pour charger les données depuis l'API ---
  const fetchAppointments = useCallback(async (currentStatusFilter: string) => {
    setLoading(true);
    setError(null);
    try {
      // Appeler le service API avec le filtre de statut
      const data = await appointmentService.getAll(currentStatusFilter); 
      setAppointments(data); // Stocker les données brutes reçues
      // Laisser l'autre useEffect gérer les filtres client (recherche, type, comm)
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des rendez-vous');
      setAppointments([]); // Vider en cas d'erreur
      setFilteredAppointments([]);
      setLoading(false);
      console.error(err);
    }
  }, []); // useCallback pour éviter recréation inutile

  // --- Effet pour le chargement initial ---
  useEffect(() => {
    fetchAppointments(statusFilter); // Charger avec le filtre initial ('all')
  }, [fetchAppointments]); // Dépend de fetchAppointments
  
  // --- Effet pour initialiser Socket.IO et écouter les événements ---
  useEffect(() => {
    // Initialiser la connexion Socket.IO
    const socket = socketService.connect();
    console.log('Socket.IO initialisé dans AppointmentList');
    
    // Fonction de callback pour la mise à jour du statut SMS
    const handleSMSStatusUpdate = (data: any) => {
      console.log('Mise à jour du statut SMS reçue dans AppointmentList:', data);
      
      // Mettre à jour le statut SMS dans la liste des rendez-vous
      setAppointments(prevAppointments => {
        return prevAppointments.map(appointment => {
          if (appointment.id === data.appointmentId) {
            return {
              ...appointment,
              sms_status: data.status
            };
          }
          return appointment;
        });
      });
    };
    
    // Ajouter l'écouteur d'événement
    socketService.addListener('sms:status-updated', handleSMSStatusUpdate);
    
    // Nettoyer l'écouteur lors du démontage du composant
    return () => {
      socketService.removeListener('sms:status-updated', handleSMSStatusUpdate);
    };
  }, []); // Exécuter une seule fois au montage du composant

  // --- Effet pour réagir au changement du filtre de statut ---
  useEffect(() => {
    // Appeler l'API à chaque changement de statusFilter
    fetchAppointments(statusFilter);
  }, [statusFilter, fetchAppointments]); // Dépend de statusFilter et fetchAppointments

  // --- Effet pour les filtres côté client (recherche, type, communication) ---
  useEffect(() => {
    let filtered = appointments; // Partir des données brutes reçues du serveur

    // Filtrer par terme de recherche (CLIENT SIDE)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        appointment =>
          appointment.name.toLowerCase().includes(term) ||
          appointment.surname.toLowerCase().includes(term) ||
          appointment.email.toLowerCase().includes(term) ||
          appointment.phone.includes(term)
      );
    }

    // Filtrer par type (CLIENT SIDE)
    if (typeFilter !== 'all') {
      filtered = filtered.filter(appointment => appointment.type === typeFilter);
    }
    
    // !!! LE FILTRE STATUS A ÉTÉ SUPPRIMÉ ICI CAR GÉRÉ PAR LE SERVEUR !!!

    // Filtrer par communication (CLIENT SIDE)
    if (communicationFilter !== 'all') {
      filtered = filtered.filter(appointment => {
        const simplifiedEmail = getSimplifiedEmailStatus(appointment.email_status);
        const simplifiedSMS = getSimplifiedSMSStatus(appointment.sms_status);

        switch (communicationFilter) {
          case 'email_sent':
            return simplifiedEmail === 'Envoyé';
          case 'email_error':
            return simplifiedEmail === 'Erreur';
          case 'sms_scheduled':
            return simplifiedSMS === 'Programmé';
          case 'sms_sent':
            return simplifiedSMS === 'Envoyé';
          case 'sms_error':
            return simplifiedSMS === 'Erreur';
          default:
            return true; // Ne filtre pas si la valeur n'est pas reconnue
        }
      });
    }

    setFilteredAppointments(filtered); // Mettre à jour la liste affichée
  }, [appointments, searchTerm, typeFilter, communicationFilter]); // Ne dépend plus de statusFilter

  // Obtenir les types uniques pour le filtre
  const uniqueTypes = Array.from(new Set(appointments.map(appointment => appointment.type)));

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="appointment-list-page">
      <div className="appointment-list-header">
        <h1>Liste des rendez-vous</h1>
        <Link to="/appointments/new" className="btn btn-primary">
          Nouveau rendez-vous
        </Link>
      </div>

      <div className="filters-container">
        <div className="search-container">
          <input
            type="text"
            placeholder="Rechercher par nom, email ou téléphone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Statut:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous</option>
            <option value="pending">En attente</option>
            <option value="confirmed">Confirmé</option>
            <option value="completed">Terminé</option>
            <option value="cancelled">Annulé</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="type-filter">Type:</label>
          <select
            id="type-filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous</option>
            {uniqueTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="communication-filter">Communication:</label>
          <select
            id="communication-filter"
            value={communicationFilter}
            onChange={(e) => setCommunicationFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous statuts comm.</option>
            <option value="email_sent">Email Envoyé</option>
            <option value="email_error">Email Erreur</option>
            <option value="sms_scheduled">SMS Programmé</option>
            <option value="sms_sent">SMS Envoyé</option>
            <option value="sms_error">SMS Erreur</option>
          </select>
        </div>
      </div>

      {filteredAppointments.length === 0 ? (
        <div className="no-appointments">
          <p>Aucun rendez-vous trouvé.</p>
        </div>
      ) : (
        <div className="appointment-grid">
          {filteredAppointments.map((appointment) => (
            <AppointmentCard key={appointment.id} appointment={appointment} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AppointmentList;
